import { useSelector } from 'react-redux';

import { currentOpenChatSelector } from '../../../../../selectors';
import { useMessageContext } from '../../../../../utils/messageContext';
import { getFileSrc } from '../../../../../utils/getFileSrc';
import { formatBytes } from '../../../../../utils/formatBytes';
import { useMemo } from 'react';
import { noop } from '../../../../../utils/noop';

interface IUseDocumentResult {
  src: string | null;
  name: string;
  size: string | undefined;

  cancelLoad: VoidFunction;
}

export const useDocument = (): IUseDocumentResult | null => {
  const {
    messageId,
    message: { files },
  } = useMessageContext();
  const document = files?.documents?.[0];

  const { pendingFiles, chatId = '' } = useSelector(currentOpenChatSelector) || {};

  return useMemo(() => {
    if (document) {
      const { uid, name, size } = document;

      return {
        src: getFileSrc(uid, chatId),
        name,
        size: size ? formatBytes(size) : undefined,
        cancelLoad: noop,
      };
    }

    const blobFiles = pendingFiles?.[messageId]?.blobFiles || [];
    const documentBlobFiles = blobFiles.filter(({ type }) => type === 'document');

    if (documentBlobFiles.length) {
      const {
        file: { name, size },
        cancellationToken,
      } = documentBlobFiles[0];

      return {
        src: null,
        name,
        size: formatBytes(size),
        cancelLoad: () => cancellationToken.cancel(),
      };
    }

    return null;
  }, [document, chatId, messageId, pendingFiles]);
};
